import React, { useState } from 'react';

type ShortcutCategory = {
  name: string;
  shortcuts: {
    keys: string[];
    description: string;
  }[];
};

const SHORTCUT_CATEGORIES: ShortcutCategory[] = [
  {
    name: 'General',
    shortcuts: [
      { keys: ['Cmd', 'P'], description: 'Quick Open file' },
      { keys: ['Cmd', 'Shift', 'P'], description: 'Command Palette' },
      { keys: ['Cmd', 'S'], description: 'Save file' },
      { keys: ['Cmd', 'Shift', 'S'], description: 'Save All' },
      { keys: ['F11'], description: 'Toggle Zen Mode' },
      { keys: ['Cmd', ','], description: 'Open Settings' },
    ],
  },
  {
    name: 'Editor',
    shortcuts: [
      { keys: ['Cmd', 'Z'], description: 'Undo' },
      { keys: ['Cmd', 'Shift', 'Z'], description: 'Redo' },
      { keys: ['Cmd', 'X'], description: 'Cut line' },
      { keys: ['Cmd', 'C'], description: 'Copy line' },
      { keys: ['Cmd', 'V'], description: 'Paste' },
      { keys: ['Cmd', 'D'], description: 'Add selection to next find match' },
      { keys: ['Cmd', 'Shift', 'L'], description: 'Select all occurrences' },
      { keys: ['Alt', 'Up'], description: 'Move line up' },
      { keys: ['Alt', 'Down'], description: 'Move line down' },
      { keys: ['Cmd', 'Shift', 'D'], description: 'Duplicate line' },
      { keys: ['Cmd', '/'], description: 'Toggle line comment' },
      { keys: ['Tab'], description: 'Indent line' },
      { keys: ['Shift', 'Tab'], description: 'Outdent line' },
      { keys: ['Cmd', '+'], description: 'Increase font size' },
      { keys: ['Cmd', '-'], description: 'Decrease font size' },
      { keys: ['Cmd', '0'], description: 'Reset font size' },
    ],
  },
  {
    name: 'Multi-Cursor',
    shortcuts: [
      { keys: ['Cmd', 'Alt', 'Up'], description: 'Add cursor above' },
      { keys: ['Cmd', 'Alt', 'Down'], description: 'Add cursor below' },
      { keys: ['Alt', 'Click'], description: 'Add cursor at click position' },
      { keys: ['Alt', 'Shift', 'Drag'], description: 'Column/Box selection' },
      { keys: ['Esc'], description: 'Remove secondary cursors' },
    ],
  },
  {
    name: 'Search & Replace',
    shortcuts: [
      { keys: ['Cmd', 'F'], description: 'Find in file' },
      { keys: ['Cmd', 'H'], description: 'Replace in file' },
      { keys: ['Cmd', 'Shift', 'F'], description: 'Find in project' },
      { keys: ['Cmd', 'Shift', 'H'], description: 'Replace in project' },
      { keys: ['F3'], description: 'Find next' },
      { keys: ['Shift', 'F3'], description: 'Find previous' },
    ],
  },
  {
    name: 'Navigation',
    shortcuts: [
      { keys: ['Cmd', 'G'], description: 'Go to line' },
      { keys: ['Cmd', 'Shift', 'O'], description: 'Go to symbol' },
      { keys: ['F12'], description: 'Go to definition' },
      { keys: ['Alt', 'F12'], description: 'Peek definition' },
      { keys: ['Shift', 'F12'], description: 'Find all references' },
      { keys: ['Cmd', 'Click'], description: 'Go to definition' },
      { keys: ['Alt', 'Left'], description: 'Go back' },
      { keys: ['Alt', 'Right'], description: 'Go forward' },
    ],
  },
  {
    name: 'Code Folding',
    shortcuts: [
      { keys: ['Cmd', 'Shift', '['], description: 'Fold region' },
      { keys: ['Cmd', 'Shift', ']'], description: 'Unfold region' },
      { keys: ['Cmd', 'K', 'Cmd', '0'], description: 'Fold all' },
      { keys: ['Cmd', 'K', 'Cmd', 'J'], description: 'Unfold all' },
      { keys: ['Cmd', 'K', 'Cmd', '1-5'], description: 'Fold to level 1-5' },
    ],
  },
  {
    name: 'Selection',
    shortcuts: [
      { keys: ['Cmd', 'A'], description: 'Select all' },
      { keys: ['Cmd', 'L'], description: 'Select line' },
      { keys: ['Shift', 'Alt', 'Right'], description: 'Expand selection' },
      { keys: ['Shift', 'Alt', 'Left'], description: 'Shrink selection' },
    ],
  },
  {
    name: 'Refactoring',
    shortcuts: [
      { keys: ['F2'], description: 'Rename symbol' },
      { keys: ['Cmd', '.'], description: 'Quick fix' },
      { keys: ['Cmd', 'Shift', 'M'], description: 'Extract to function' },
      { keys: ['Alt', 'Shift', 'V'], description: 'Extract to variable' },
      { keys: ['Alt', 'Shift', 'O'], description: 'Organize imports' },
      { keys: ['Cmd', 'Shift', 'F'], description: 'Format document' },
    ],
  },
  {
    name: 'Git',
    shortcuts: [
      { keys: ['Cmd', 'Shift', 'G'], description: 'Open Source Control' },
    ],
  },
  {
    name: 'Terminal',
    shortcuts: [
      { keys: ['Ctrl', '`'], description: 'Toggle terminal' },
      { keys: ['Cmd', 'Shift', 'C'], description: 'Copy in terminal' },
      { keys: ['Cmd', 'Shift', 'V'], description: 'Paste in terminal' },
    ],
  },
];

type ShortcutsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const ShortcutsModal = React.memo(function ShortcutsModal({
  isOpen,
  onClose,
}: ShortcutsModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  if (!isOpen) return null;

  // Filter shortcuts based on search
  const filteredCategories = SHORTCUT_CATEGORIES.map((category) => ({
    ...category,
    shortcuts: category.shortcuts.filter(
      (shortcut) =>
        shortcut.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        shortcut.keys.join(' ').toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(
    (category) =>
      category.shortcuts.length > 0 &&
      (selectedCategory === null || category.name === selectedCategory)
  );

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal shortcuts-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-modal-title"
      >
        <div className="modal-header">
          <div className="modal-title" id="shortcuts-modal-title">Keyboard Shortcuts</div>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            x
          </button>
        </div>

        <div className="shortcuts-search">
          <input
            type="text"
            placeholder="Search shortcuts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            aria-label="Search keyboard shortcuts"
          />
        </div>

        <div className="shortcuts-categories">
          <button
            className={`shortcuts-category-btn ${selectedCategory === null ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            All
          </button>
          {SHORTCUT_CATEGORIES.map((category) => (
            <button
              key={category.name}
              className={`shortcuts-category-btn ${selectedCategory === category.name ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.name)}
            >
              {category.name}
            </button>
          ))}
        </div>

        <div className="shortcuts-content">
          {filteredCategories.map((category) => (
            <div key={category.name} className="shortcuts-section">
              <h3 className="shortcuts-section-title">{category.name}</h3>
              <div className="shortcuts-list">
                {category.shortcuts.map((shortcut, index) => (
                  <div key={index} className="shortcut-item">
                    <div className="shortcut-keys">
                      {shortcut.keys.map((key, keyIndex) => (
                        <React.Fragment key={keyIndex}>
                          <kbd className="shortcut-key">{key}</kbd>
                          {keyIndex < shortcut.keys.length - 1 && (
                            <span className="shortcut-plus">+</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="shortcut-description">{shortcut.description}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {filteredCategories.length === 0 && (
            <div className="shortcuts-empty">
              No shortcuts found matching "{searchQuery}"
            </div>
          )}
        </div>

        <div className="modal-footer">
          <span className="shortcuts-tip">
            Tip: Press <kbd>Cmd</kbd>+<kbd>Shift</kbd>+<kbd>P</kbd> to open Command Palette
          </span>
          <button className="pill-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

export default ShortcutsModal;
